import { Component, computed, inject, input, model, output, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';

import { AddPlanningSpaceResponse } from '@gfe/core/models/planning-space/planning-space.model';
import { ICONS } from '@gfe/core/services/icons/icons.service';
import { NotificationService } from '@gfe/core/services/notification/notification.service';
import { PlansService } from '@gfe/core/services/plans/plans.service';
import { ModalComponent, ModalConfig } from '@gfe/shared/components/modal/modal.component';

import {
  PlanDetails,
  PlanDetailsFormComponent
} from '@features/components/plan/plan-details-form/plan-details-form.component';

/**
 * Self-contained duplicate-plan UX shared by every entry point (opened-plan
 * toolbar, plans table row menu, planning-space graph node menu). It owns the
 * naming modal, the in-flight progress modal, the backend call and the result
 * notifications. The parent only opens it and reacts to `duplicated` (e.g.
 * navigate to, or refresh the list to reveal, the new plan).
 */
@Component({
  selector: 'app-duplicate-plan-flow',
  imports: [ModalComponent, PlanDetailsFormComponent, TranslatePipe],
  templateUrl: './duplicate-plan-flow.component.html',
  styles: ``
})
export class DuplicatePlanFlowComponent {
  private readonly plansService = inject(PlansService);
  private readonly notificationService = inject(NotificationService);
  private readonly translate = inject(TranslateService);

  planId = input<string>('');
  sourceName = input<string>('');
  sourceDescription = input<string>('');
  /**
   * When true, the in-progress message tells the user they'll be redirected to
   * the new plan once it's ready (the opened-plan view navigates on success).
   * When false (e.g. the list/graph view, which just refreshes), that line is
   * omitted.
   */
  redirectOnComplete = input<boolean>(false);

  /** Two-way: the parent opens the flow; the flow closes itself on cancel/success. */
  open = model<boolean>(false);

  duplicated = output<AddPlanningSpaceResponse>();

  protected readonly isDuplicating = signal(false);

  protected readonly ICONS = ICONS;

  protected readonly suggestedName = computed(() =>
    this.sourceName() ? `${this.sourceName()} (copy)` : ''
  );

  protected readonly formModalConfig: ModalConfig = {
    dismissible: true,
    closeOnBackdropClick: true
  };
  // Progress modal shown while the request is in flight: cannot be dismissed so
  // the operation can't be abandoned mid-creation.
  protected readonly progressModalConfig: ModalConfig = {
    dismissible: false,
    closeOnBackdropClick: false
  };

  protected closeForm(): void {
    if (this.isDuplicating()) {
      return;
    }
    this.open.set(false);
  }

  protected onDuplicate(data: PlanDetails): void {
    const planId = this.planId();
    if (!planId || this.isDuplicating()) {
      return;
    }

    this.isDuplicating.set(true);
    this.plansService
      .duplicatePlan$({
        planId,
        name: data.name,
        description: data.description
      })
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.isDuplicating.set(false);
          this.open.set(false);
          this.notificationService.success(
            this.translate.instant('features.plan.duplicate.title'),
            this.translate.instant('features.plan.duplicate.success')
          );
          this.duplicated.emit(response);
        },
        error: (error) => {
          console.error('Error duplicating plan:', error);
          this.isDuplicating.set(false);
          this.notificationService.error(
            this.translate.instant('features.plan.duplicate.title'),
            this.translate.instant('features.plan.duplicate.error')
          );
        }
      });
  }
}
